import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TourismPackage } from "@/components/tourism-packages/types";
import { toast } from "sonner";

export interface TourismPackageData {
  package_number: string;
  title: string;
  destination: string;
  country_id?: string;
  city_id?: string;
  duration: string;
  price: number;
  currency?: string;
  status: 'active' | 'draft' | 'expired';
  payment_status: 'paid' | 'pending' | 'unpaid';
  type: 'employee' | 'b2b';
  description?: string;
  highlights?: string[];
  included_services?: string[];
  excluded_services?: string[];
  images?: string[];
  rating?: number;
  review_count?: number;
  max_capacity?: number;
  min_capacity?: number;
  client_name?: string;
  client_id?: string;
}

export const useTourismPackagesReal = () => {
  const queryClient = useQueryClient();

  // جلب جميع الباقات
  const {
    data: packages = [],
    isLoading,
    error
  } = useQuery({
    queryKey: ["tourism-packages"],
    queryFn: async (): Promise<TourismPackage[]> => {
      const { data, error } = await supabase
        .from("tourism_packages")
        .select(`
          *,
          countries:country_id(name_ar, name_en),
          cities:city_id(name_ar, name_en)
        `)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching tourism packages:", error);
        throw error;
      }

      return data.map(pkg => ({
        ...pkg,
        country_name: pkg.countries?.name_ar,
        city_name: pkg.cities?.name_ar,
        status: pkg.status as 'active' | 'draft' | 'expired',
        payment_status: pkg.payment_status as 'paid' | 'pending' | 'unpaid',
        type: pkg.type as 'employee' | 'b2b'
      })) as TourismPackage[];
    }
  });

  // إنشاء باقة جديدة
  const createPackage = useMutation({
    mutationFn: async (packageData: TourismPackageData) => {
      const { data: currentEmployee } = await supabase
        .from("employees")
        .select("id")
        .eq("user_id", (await supabase.auth.getUser()).data.user?.id)
        .single();

      const { data, error } = await supabase
        .from("tourism_packages")
        .insert([{
          ...packageData,
          created_by: currentEmployee?.id
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tourism-packages"] });
      queryClient.invalidateQueries({ queryKey: ["package-search"] });
      toast.success("تم إنشاء الباقة بنجاح");
    },
    onError: (error) => {
      console.error("Error creating package:", error);
      toast.error("حدث خطأ أثناء إنشاء الباقة");
    }
  });

  // تحديث باقة
  const updatePackage = useMutation({
    mutationFn: async ({ id, ...packageData }: TourismPackageData & { id: string }) => {
      const { data, error } = await supabase
        .from("tourism_packages")
        .update(packageData)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tourism-packages"] });
      queryClient.invalidateQueries({ queryKey: ["package-search"] });
      toast.success("تم تحديث الباقة بنجاح");
    },
    onError: (error) => {
      console.error("Error updating package:", error);
      toast.error("حدث خطأ أثناء تحديث الباقة");
    }
  });

  // حذف باقة
  const deletePackage = useMutation({
    mutationFn: async (packageId: string) => {
      const { error } = await supabase
        .from("tourism_packages")
        .delete()
        .eq("id", packageId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tourism-packages"] });
      queryClient.invalidateQueries({ queryKey: ["package-search"] });
      toast.success("تم حذف الباقة بنجاح");
    },
    onError: (error) => {
      console.error("Error deleting package:", error);
      toast.error("حدث خطأ أثناء حذف الباقة");
    }
  });

  // البحث والفلترة
  const [searchTerm, setSearchTerm] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");

  const filteredPackages = packages.filter(pkg => {
    const matchesSearch = pkg.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         pkg.destination.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesClient = !clientSearch || 
                         (pkg.client_name && pkg.client_name.toLowerCase().includes(clientSearch.toLowerCase()));
    const matchesStatus = filterStatus === "all" || pkg.status === filterStatus;
    const matchesType = filterType === "all" || pkg.type === filterType;
    
    return matchesSearch && matchesClient && matchesStatus && matchesType;
  });

  const stats = {
    total: packages.length,
    active: packages.filter(p => p.status === 'active').length,
    draft: packages.filter(p => p.status === 'draft').length,
    expired: packages.filter(p => p.status === 'expired').length,
  };

  return {
    packages,
    filteredPackages,
    stats,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    clientSearch,
    setClientSearch,
    filterStatus,
    setFilterStatus,
    filterType,
    setFilterType,
    createPackage: createPackage.mutate,
    updatePackage: updatePackage.mutate,
    deletePackage: deletePackage.mutate,
    isCreating: createPackage.isPending,
    isUpdating: updatePackage.isPending,
    isDeleting: deletePackage.isPending
  };
};

// Hook للبحث المتقدم في الباقات
export const usePackageSearch = () => {
  return useQuery({
    queryKey: ["package-search"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tourism_packages")
        .select(`
          *,
          countries:country_id(id, name_ar, name_en),
          cities:city_id(id, name_ar, name_en)
        `)
        .eq("status", "active")
        .order("created_at", { ascending: false });

      if (error) throw error;

      return data.map(pkg => ({
        ...pkg,
        rating: pkg.rating || Math.floor(Math.random() * 2) + 4, // 4-5 نجوم
        reviewCount: pkg.review_count || Math.floor(Math.random() * 500) + 50,
        imageUrl: pkg.images?.[0] || `/api/placeholder/400/300?text=${encodeURIComponent(pkg.title)}`,
        highlights: pkg.highlights || [
          'إفطار مجاني',
          'واي فاي مجاني',
          'مسبح',
          'سبا'
        ]
      }));
    }
  });
};